import {
  DateAndTime,
  isValidIsoString,
  TimeQuestionConfig,
  TimeResponseState,
} from '@gripp/shared-logic';
import moment from 'moment';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { DatePickerSelector, useFormContext } from '../../../../../forms';
import {
  RoutineQuestionExecutionForm,
  RoutineQuestionExecutionFormProps,
} from '../../types';

export const TimeAndDate: RoutineQuestionExecutionForm<
  TimeQuestionConfig,
  TimeResponseState
> = (
  props: RoutineQuestionExecutionFormProps<
    TimeQuestionConfig,
    TimeResponseState
  >
) => {
  const { t } = useTranslation();
  const dateFieldPrefix = `responses.${props.responseIndex}.timeTypeValue.date`;
  const timeFieldPrefix = `responses.${props.responseIndex}.timeTypeValue.time`;
  const { control, setValue } = useFormContext();
  // Forces the web time picker to re-read its initial value after an edit
  // prefill (it seeds internal state once on mount). Mirrors TimeOnly.
  const keyRefresh = useRef<string>('timeAndDate');

  useEffect(() => {
    const stored = props.currentState?.state?.responses?.[props.responseIndex]
      ?.timeTypeValue as DateAndTime | undefined;

    if (props.isIssueEditRoutine) {
      const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
      const storedDate = stored?.date;
      if (
        typeof storedDate === 'string' &&
        dateRegex.test(storedDate) &&
        moment(storedDate, 'MM/DD/YYYY', true).isValid()
      ) {
        setValue(dateFieldPrefix, storedDate, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
      if (isValidIsoString(stored?.time)) {
        setValue(timeFieldPrefix, stored?.time, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
      keyRefresh.current = 'timeAndDate-refresh';
    } else {
      // Default the date to today (mirrors DateOnly); the time is left empty
      // for the user to set.
      setValue(dateFieldPrefix, moment().format('MM/DD/YYYY'), {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [props.currentState, props.isIssueEditRoutine]);

  return (
    <View style={styles.container}>
      <DatePickerSelector
        name={dateFieldPrefix}
        setValue={setValue}
        control={control}
        label={t('routines.time.message.date')}
        placeholder="MM/DD/YYYY"
        maxYears={11}
      />
      <DatePickerSelector
        key={keyRefresh.current}
        name={timeFieldPrefix}
        setValue={setValue}
        control={control}
        label={t('routines.time.message.time')}
        placeholder="00:00"
        mode="time"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    rowGap: 12,
    width: '100%',
  },
});
